import { CircleAlert, Clock, UserRound } from 'lucide-react';
import { Popover } from 'radix-ui';
import type { Blocker, CustomerDeskView } from '../api';
import '../blocked-chip.css';
import { useAgentName, withAgent } from '../agent-name';

const owners = (agent: string): Record<Blocker['owner'], string> => ({ you: 'You', amy: agent, customer: 'The customer' });
/** Codes that clear by themselves; shown quieter than the ones a person must act on. */
const passive = new Set<Blocker['code']>(['outside_window', 'daily_cap', 'promise_active', 'out_of_office', 'draft_withdrawn', 'payment_claimed']);
export const blockerTitle: Record<Blocker['code'], string> = {
  no_contact: 'No billing contact', contact_bouncing: 'Contact bouncing', dispute_open: 'Dispute open', payment_claimed: 'Payment claimed',
  promise_active: 'Promise to pay', customer_replied: 'Customer replied', missing_po: 'Missing PO', vendor_compliance: 'Vendor compliance',
  out_of_office: 'Out of office', do_not_call: 'Do not call', paused_manual: 'Paused', source_stale: 'Ledger out of date',
  outside_window: 'Send scheduled', daily_cap: 'Contact limit', needs_review: 'Needs a person', mailbox_disconnected: 'Mailbox disconnected',
  draft_withdrawn: 'Draft withdrawn',
};
const shortDay = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

/**
 * One reason something is not moving, the same everywhere (#109). The chip names it; the popover says since when,
 * who acts, what to do and what clears it, with a link to the fix where one exists. The same reason on several invoices
 * is one chip ("Paused · 4") whose popover lists each. Keyboard: Enter/Space opens, Escape closes.
 */
export function BlockedChip({ blocker, others = [] }: { blocker: Blocker; others?: Blocker[] }) {
  const agent = useAgentName();
  const all = [blocker, ...others];
  const since = all.map(b => b.since).filter((d): d is string => Boolean(d)).sort()[0] ?? null;
  const title = blockerTitle[blocker.code];
  return <Popover.Root>
    <Popover.Trigger asChild>
      <button type="button" className={`blocked-chip ${passive.has(blocker.code) ? 'passive' : 'active'}`} data-code={blocker.code}
        aria-label={all.length > 1 ? `${title}: ${all.length} items` : `${title}: ${withAgent(blocker.detail, agent)}`}>
        <CircleAlert size={12} aria-hidden /><span>{title}</span>{all.length > 1 && <span className="blocked-chip-count">{all.length}</span>}
      </button>
    </Popover.Trigger>
    <Popover.Portal>
      <Popover.Content className="blocked-pop" sideOffset={6} align="start" collisionPadding={12} role="dialog"
        aria-label={title}>
        <strong className="blocked-pop-title">{title}</strong>
        {all.length > 1 ? <ul className="blocked-pop-list">{[...new Set(all.map(b => withAgent(b.detail, agent)))].slice(0, 6).map(d => <li key={d}>{d}</li>)}</ul>
          : <p className="blocked-pop-detail">{withAgent(blocker.detail, agent)}</p>}
        <dl>
          {since && <div><dt><Clock size={12} aria-hidden />Since</dt><dd>{shortDay(since)}</dd></div>}
          <div><dt><UserRound size={12} aria-hidden />Who acts</dt><dd>{owners(agent)[blocker.owner]}</dd></div>
          <div><dt>Next</dt><dd>{withAgent(blocker.nextAction, agent)}</dd></div>
          <div><dt>Clears when</dt><dd>{withAgent(blocker.clearsWhen, agent)}</dd></div>
        </dl>
        {blocker.fix && <a className="blocked-pop-fix" href={blocker.fix.href}>{blocker.fix.label}</a>}
        <Popover.Arrow className="blocked-pop-arrow" />
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>;
}

/** Blockers with the same reason, together: one entry per code, in first-seen order. */
export function groupBlockers(blockers: Blocker[]): Blocker[][] {
  const byCode = new Map<Blocker['code'], Blocker[]>();
  for (const b of blockers) byCode.set(b.code, [...(byCode.get(b.code) ?? []), b]);
  return [...byCode.values()];
}

/** The keys of the first row for each customer, so a list says why a customer is held once. */
export function firstRowPerCustomer(rows: { key: string; customerId?: string | null }[]): Set<string> {
  const seen = new Set<string>(); const first = new Set<string>();
  for (const row of rows) { const who = row.customerId ?? row.key; if (!seen.has(who)) { seen.add(who); first.add(row.key); } }
  return first;
}

/** Every blocker as chips, most serious first; nothing renders when there are none. */
export function BlockedChips({ blockers, label = 'Why this is not moving' }: { blockers: Blocker[] | undefined; label?: string }) {
  if (!blockers?.length) return null;
  return <div className="blocked-chips" role="list" aria-label={label}>
    {groupBlockers(blockers).map(([first, ...rest]) => <span role="listitem" key={first!.code}><BlockedChip blocker={first!} others={rest} /></span>)}
  </div>;
}

/**
 * A customer's states in one row: the blockers (each opens its reason), then any status a blocker does not already say.
 * A promise, a reply hold or a dispute is shown once, as its blocker; a broken promise or a suspended account stays a badge.
 */
export function DeskStates({ blockers, badges, status }:
  { blockers: Blocker[]; badges: CustomerDeskView['badges']; status: string }) {
  const codes = new Set(blockers.map(b => b.code));
  const sorted = [...blockers].sort((a, b) => Number(passive.has(a.code)) - Number(passive.has(b.code)));
  const extra: [string, string][] = [];
  if (status !== 'active') extra.push([status, status === 'suspended' ? 'Suspended' : 'Churned']);
  if (badges.brokenPromise) extra.push(['broken', 'Broken promise']);
  if (badges.dispute && !codes.has('dispute_open')) extra.push(['dispute', 'Dispute']);
  if (badges.promise && !codes.has('promise_active')) extra.push(['promise', 'Promise to pay']);
  // A hold is already explained by the blocker that caused it (a reply, a promise, a manual pause).
  if (badges.paused && !badges.dispute && !blockers.length) extra.push(['paused', 'Paused']);
  if (!sorted.length && !extra.length) return null;
  return <div className="blocked-chips desk-states" role="list" aria-label="Why this is not moving">
    {groupBlockers(sorted).map(([first, ...rest]) => <span role="listitem" key={first!.code}><BlockedChip blocker={first!} others={rest} /></span>)}
    {extra.map(([kind, label]) => <span role="listitem" key={kind} className={`state-chip ${kind}`}>{label}</span>)}
  </div>;
}
